import { provideCloudinaryLoader } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import {
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaV3Provider,
} from '@angular/fire/app-check';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, Router, RouteReuseStrategy } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  AlertController,
  IonicRouteStrategy,
  ModalController,
  PopoverController,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { register as registerSwiper } from 'swiper/element/bundle';

import { ENVIRONMENT } from '../../common/model/common-environment';
import { AppCheckInterceptor } from '../../common/services/app-check-interceptor.service';
import { HttpInterceptorService } from '../../common/services/http-interceptor.service';
import { LoadingStateService } from '../../common/services/loading-state.service';
import { VERSION } from '../../common/version';
import { AppComponent } from './app/app.component';
import { routes } from './app/routes';
import { environment } from './environments/environment';

Sentry.init({
  dsn: 'https://d521edfb075f4f689b671524918d9155@o4504824450908160.ingest.sentry.io/4504826246987776',
  integrations: [Sentry.browserTracingIntegration()],
  tracePropagationTargets: [environment.apiRestEndpoint],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  release: VERSION.version,
  environment: environment.env,
  sendClientReports: false,
  enabled: environment.env === 'production' || environment.env === 'staging',
});

registerSwiper();

const bootstrap = (): void => {
  void bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      ModalController,
      PopoverController,
      AlertController,
      provideFirebaseApp(() =>
        initializeApp(environment.firebaseOptions, environment.firebaseAppName),
      ),
      provideAppCheck(() =>
        initializeAppCheck(getApp(environment.firebaseAppName), {
          provider: new ReCaptchaV3Provider(environment.recaptchaV3SiteKey),
          isTokenAutoRefreshEnabled: true,
        }),
      ),
      { provide: ENVIRONMENT, useValue: environment },
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AppCheckInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpInterceptorService,
        multi: true,
      },
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler({ showDialog: false }),
      },
      { provide: Sentry.TraceService, deps: [Router] },
      provideCloudinaryLoader('https://res.cloudinary.com/ledzaphter'),
      provideHttpClient(),
      provideIonicAngular({ mode: 'md' }),
      provideRouter(routes),
      provideServiceWorker('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
      provideCloudinaryLoader('https://res.cloudinary.com/ledzaphter'),
      provideAppInitializer(() => {
        inject(LoadingStateService);
        inject(Sentry.TraceService);
      }),
    ],
  });
};

if (document.readyState === 'complete') {
  bootstrap();
} else {
  document.addEventListener('DOMContentLoaded', bootstrap);
}
